/**
 * Devices Page
 *
 * Provides device configuration interface per tank:
 * - List of configured devices
 * - Add new device
 * - Edit device credentials
 * - Test device connection
 * - Delete device
 */

import React, { useState, useCallback } from 'react';
import {
  Container,
  Title,
  Stack,
  Paper,
  Table,
  Button,
  Group,
  Text,
  Badge,
  Modal,
  TextInput,
  PasswordInput,
  Alert,
  Loader,
  Box,
  Radio,
  Switch,
  Divider,
  List,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconPlus,
  IconAlertCircle,
  IconCheck,
  IconX,
  IconInfoCircle,
  IconPencil,
} from '@tabler/icons-react';
import { useTank } from '../contexts/TankContext';
import { NoTankSelected } from '@/components/common/NoTankSelected';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { tankParametersQueryKey } from '../api/parameters';
import { DeviceConfig, DeviceType } from '../types/devices';
import { DeviceCard, DeviceDetailModal, DeviceActionsMenu, PumpAssignmentOverlay } from '../components/devices';
import { DEVICE_TYPE_OPTIONS, KAMOER_DEVICE_TYPES } from '../features/devices/constants';
import { buildCredentials, validateForm, CredentialFields } from '../features/devices/credentialFields';
import { AddDeviceForm } from '../features/devices/AddDeviceForm';
import { AddDeviceDuplicatePayload } from '../features/devices/useAddDeviceFlow';

// Delay for modal transitions to prevent UI glitches
const MODAL_TRANSITION_DELAY_MS = 150;

export const DevicesPage: React.FC = () => {
  const { selectedTank } = useTank();

  // Fetch devices for selected tank
  const {
    data: devices = [],
    isLoading,
    error,
    refetch,
  } = useQuery<DeviceConfig[]>({
    queryKey: ['devices', selectedTank?.id],
    queryFn: async () => {
      if (!selectedTank) return [];
      const response = await api.get<{ devices: DeviceConfig[] }>(
        `/api/tanks/${selectedTank.id}/devices`
      );
      return response.devices;
    },
    enabled: !!selectedTank,
  });

  const queryClient = useQueryClient();

  // Refetch devices and invalidate tank-parameters so nav capability flags
  // (e.g. Dosing) update immediately after a device mutation instead of
  // waiting out the useTankParameters staleTime.
  const refreshDevicesAndCapabilities = async () => {
    await refetch();
    queryClient.invalidateQueries({ queryKey: tankParametersQueryKey(selectedTank?.id) });
  };

  // Modal states
  const [addModalOpened, setAddModalOpened] = useState(false);
  const [editModalOpened, setEditModalOpened] = useState(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [deleteMode, setDeleteMode] = useState<'delete_data' | 'disable' | null>(null);
  const [testModalOpened, setTestModalOpened] = useState(false);
  const [selectedForEdit, setSelectedForEdit] = useState<DeviceConfig | null>(null);
  const [selectedForDelete, setSelectedForDelete] = useState<DeviceConfig | null>(null);
  const [selectedForTest, setSelectedForTest] = useState<DeviceConfig | null>(null);

  // Duplicate "update existing" flow (signalled by the add flow via onDuplicate)
  const [showUpdateExistingModal, setShowUpdateExistingModal] = useState(false);
  const [duplicateDevices, setDuplicateDevices] = useState<DeviceConfig[]>([]);
  const [duplicatePayload, setDuplicatePayload] = useState<AddDeviceDuplicatePayload | null>(null);

  // Detail modal state (mobile only)
  const [detailModalOpened, setDetailModalOpened] = useState(false);
  const [selectedForDetail, setSelectedForDetail] = useState<DeviceConfig | null>(null);

  // Pump assignment overlay state (dosetronic and apex_fusion)
  const [pumpAssignmentDevice, setPumpAssignmentDevice] = useState<DeviceConfig | null>(null);

  // Edit form states
  const [deviceType, setDeviceType] = useState<DeviceType | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [enableWaterTests, setEnableWaterTests] = useState(true);
  const [enableMaintenance, setEnableMaintenance] = useState(true);
  const [isEnabled, setIsEnabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [editAccountEmail, setEditAccountEmail] = useState('');
  const [editAccountPassword, setEditAccountPassword] = useState('');

  // Test result state (test-existing-device modal)
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  // Helper: check if a device is a linked Kamoer device (credentials managed by provider account)
  const isLinkedKamoerDevice = (device: DeviceConfig | null): boolean => {
    if (!device) return false;
    return !!device.provider_account_id &&
      KAMOER_DEVICE_TYPES.includes(device.device_type as DeviceType);
  };

  // Reset edit form
  const resetForm = () => {
    setDeviceType(null);
    setDisplayName('');
    setUsername('');
    setEmail('');
    setPassword('');
    setEnableWaterTests(true);
    setEnableMaintenance(true);
    setFormError(null);
    setTestResult(null);
    setEditAccountEmail('');
    setEditAccountPassword('');
  };

  // Handle add device
  const handleAdd = () => {
    setAddModalOpened(true);
  };

  // Handle edit device
  const handleEdit = (device: DeviceConfig) => {
    setSelectedForEdit(device);
    setDeviceType(device.device_type as DeviceType);
    setDisplayName(device.display_name);
    setIsEnabled(device.is_enabled);
    setFormError(null);
    setTestResult(null);

    // Initialize linked Kamoer account fields
    if (device.provider_account_id && KAMOER_DEVICE_TYPES.includes(device.device_type as DeviceType)) {
      setEditAccountEmail(device.provider_account?.account_identifier ?? '');
      setEditAccountPassword('');
    } else {
      setEditAccountEmail('');
      setEditAccountPassword('');
    }

    // Note: We don't pre-fill credentials for security
    setEditModalOpened(true);
  };

  // Handle delete device
  const handleDelete = (device: DeviceConfig) => {
    setSelectedForDelete(device);
    setDeleteModalOpened(true);
  };

  // Handle test connection
  const handleTest = (device: DeviceConfig) => {
    setSelectedForTest(device);
    setTestResult(null);
    setTestModalOpened(true);
  };

  // Handle card tap (mobile) - opens detail modal
  const handleCardTap = useCallback((device: DeviceConfig) => {
    setSelectedForDetail(device);
    setDetailModalOpened(true);
  }, []);

  // Handle action from detail modal - memoized to prevent unnecessary re-renders
  const handleDetailAction = useCallback((
    action: 'test' | 'edit' | 'delete',
    device: DeviceConfig
  ) => {
    setDetailModalOpened(false);
    // Small delay to let detail modal close before opening action modal
    setTimeout(() => {
      switch (action) {
        case 'test':
          handleTest(device);
          break;
        case 'edit':
          handleEdit(device);
          break;
        case 'delete':
          handleDelete(device);
          break;
      }
    }, MODAL_TRANSITION_DELAY_MS);
  }, []);

  // Memoized callbacks for DeviceDetailModal to prevent re-renders
  const handleDetailClose = useCallback(() => setDetailModalOpened(false), []);
  const handleDetailTest = useCallback((device: DeviceConfig) => handleDetailAction('test', device), [handleDetailAction]);
  const handleDetailEdit = useCallback((device: DeviceConfig) => handleDetailAction('edit', device), [handleDetailAction]);
  const handleDetailDelete = useCallback((device: DeviceConfig) => handleDetailAction('delete', device), [handleDetailAction]);

  // The add flow signals a duplicate device type: open the update-existing modal.
  const handleDuplicate = (existing: DeviceConfig[], payload: AddDeviceDuplicatePayload) => {
    setDuplicateDevices(existing);
    setDuplicatePayload(payload);
    setFormError(null);
    setShowUpdateExistingModal(true);
  };

  // Handle updating existing device(s) with new credentials
  const handleUpdateExisting = async () => {
    if (duplicateDevices.length === 0 || !selectedTank || !duplicatePayload) return;

    setIsSubmitting(true);
    setFormError(null);

    try {
      // Update all existing devices with the credentials captured by the add flow
      for (const device of duplicateDevices) {
        await api.put(
          `/api/tanks/${selectedTank.id}/devices/${device.id}`,
          {
            is_enabled: true, // Re-enable if was disabled
            credentials: duplicatePayload.credentials,
          }
        );
      }

      await refreshDevicesAndCapabilities();

      // Close both modals and reset state
      setShowUpdateExistingModal(false);
      setDuplicateDevices([]);
      setDuplicatePayload(null);
      setAddModalOpened(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update device';
      console.error('Update existing device error:', err);
      setFormError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Submit edit form
  const handleSubmitEdit = async () => {
    if (!selectedTank || !selectedForEdit || !deviceType) return;

    const linkedKamoer = isLinkedKamoerDevice(selectedForEdit);

    // Skip credential validation for linked Kamoer devices
    const validationError = validateForm(
      deviceType,
      { displayName, username, email, password },
      { skipCredentials: linkedKamoer },
    );
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setIsSubmitting(true);
    setFormError(null);

    try {
      if (linkedKamoer) {
        const providerAccountId = selectedForEdit.provider_account_id;
        if (!providerAccountId) {
          setFormError('Kamoer account is missing for this linked device.');
          setIsSubmitting(false);
          return;
        }

        const currentEmail = selectedForEdit.provider_account?.account_identifier ?? '';
        const nextEmail = editAccountEmail.trim();
        const nextPassword = editAccountPassword.trim();
        const emailChanged = nextEmail !== currentEmail;
        const passwordChanged = nextPassword.length > 0;

        if (!nextEmail) {
          setFormError('Kamoer email is required.');
          setIsSubmitting(false);
          return;
        }

        if (emailChanged && !passwordChanged) {
          setFormError('Enter the Kamoer password when changing the account email.');
          setIsSubmitting(false);
          return;
        }

        // Update provider account credentials if changed
        if (emailChanged || passwordChanged) {
          try {
            await api.put(
              `/api/tanks/${selectedTank.id}/provider-accounts/${providerAccountId}`,
              {
                ...(emailChanged ? { email: nextEmail } : {}),
                ...(passwordChanged ? { password: nextPassword } : {}),
              },
            );
          } catch (err) {
            setFormError(err instanceof Error ? err.message : 'Failed to update Kamoer credentials');
            setIsSubmitting(false);
            return;
          }
        }

        // Update device-specific fields
        try {
          await api.put(`/api/tanks/${selectedTank.id}/devices/${selectedForEdit.id}`, {
            display_name: displayName.trim(),
            is_enabled: isEnabled,
          });
        } catch (err) {
          setFormError('Kamoer credentials updated but device settings failed to save. Please try again.');
          setIsSubmitting(false);
          return;
        }

        await refreshDevicesAndCapabilities();
        setEditModalOpened(false);
        setSelectedForEdit(null);
        resetForm();
        return;
      }

      // Non-linked device: existing behavior
      const data: Record<string, unknown> = {
        display_name: displayName.trim(),
        is_enabled: isEnabled,
      };

      data.credentials = buildCredentials(deviceType, {
        username,
        email,
        password,
        enableWaterTests,
        enableMaintenance,
        selectedAquaWizDevice: null,
      });

      await api.put(`/api/tanks/${selectedTank.id}/devices/${selectedForEdit.id}`, data);
      await refreshDevicesAndCapabilities();
      setEditModalOpened(false);
      setSelectedForEdit(null);
      resetForm();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to update device');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Submit delete
  const handleSubmitDelete = async () => {
    if (!selectedForDelete || !selectedTank || deleteMode === null) return;

    setIsSubmitting(true);
    setFormError(null);

    try {
      await api.delete(
        `/api/tanks/${selectedTank.id}/devices/${selectedForDelete.id}?delete_data=${deleteMode === 'delete_data'}`
      );

      // Refresh device list
      await refreshDevicesAndCapabilities();

      // Close modal and reset state
      setDeleteModalOpened(false);
      setSelectedForDelete(null);
      setDeleteMode(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete device';
      console.error('Delete device error:', err);
      setFormError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Test connection
  const handleSubmitTest = async () => {
    if (!selectedTank || !selectedForTest) return;

    setIsSubmitting(true);
    setTestResult(null);

    try {
      const response = await api.post<{ success: boolean; message: string }>(
        `/api/tanks/${selectedTank.id}/devices/${selectedForTest.id}/test`,
        {}
      );
      setTestResult(response);
    } catch (err) {
      setTestResult({
        success: false,
        message: err instanceof Error ? err.message : 'Connection test failed',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Format device type for display in the table, with optional provider account annotation. */
  const getDeviceTypeDisplay = (device: DeviceConfig): React.ReactNode => {
    const option = DEVICE_TYPE_OPTIONS.find(
      (opt) => opt.value === device.device_type
    );
    let label: string;
    if (option) {
      label = option.label;
    } else if (device.device_type === 'alkatronic') {
      label = 'Alkatronic';
    } else if (device.device_type === 'dosetronic') {
      label = 'Dosetronic';
    } else if (device.device_type === 'mastertronic') {
      label = 'Mastertronic';
    } else if (device.device_type === 'kamoer_kh') {
      label = 'Kamoer KH Carer';
    } else if (device.device_type === 'kamoer_stp') {
      label = 'Kamoer STP Connect';
    } else if (device.device_type === 'kamoer_p4') {
      label = 'Kamoer P4 Pro';
    } else {
      label = device.device_type;
    }

    if (device.provider_account?.account_identifier) {
      return (
        <>
          <Text size="sm" c="dimmed" component="span">{label}</Text>
          <Text size="xs" c="dimmed" component="span" ml={4}>
            (via {device.provider_account.account_identifier})
          </Text>
        </>
      );
    }

    return <Text size="sm" c="dimmed">{label}</Text>;
  };

  if (!selectedTank) {
    return (
      <Container size="lg" py="xl">
        <NoTankSelected purpose="to configure its devices" />
      </Container>
    );
  }

  // Compute whether the device being edited is a linked Kamoer device
  const editingLinkedKamoer = isLinkedKamoerDevice(selectedForEdit);

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Title order={2}>Device Configuration</Title>
            <Text c="dimmed" size="sm">
              Tank: {selectedTank.name}
            </Text>
          </div>
          <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
            Add Device
          </Button>
        </Group>

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            {error instanceof Error ? error.message : 'Failed to load devices'}
          </Alert>
        )}

        {isLoading ? (
          <Paper p="xl" withBorder>
            <Group justify="center">
              <Loader />
            </Group>
          </Paper>
        ) : devices.length === 0 ? (
          <Paper p="xl" withBorder>
            <Stack align="center" gap="md">
              <Text c="dimmed">No devices configured for this tank.</Text>
              <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
                Add Your First Device
              </Button>
            </Stack>
          </Paper>
        ) : (
          <>
            {/* Desktop: Table Layout */}
            <Box visibleFrom="sm">
              <Paper withBorder>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Device</Table.Th>
                      <Table.Th>Type</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Last Sync</Table.Th>
                      <Table.Th style={{ width: 60 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {devices.map((device) => (
                      <Table.Tr key={device.id}>
                        <Table.Td>
                          <Text fw={500}>{device.display_name}</Text>
                        </Table.Td>
                        <Table.Td>
                          {getDeviceTypeDisplay(device)}
                        </Table.Td>
                        <Table.Td>
                          <Badge color={device.is_enabled ? 'green' : 'gray'}>
                            {device.is_enabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            {device.last_successful_sync
                              ? new Date(device.last_successful_sync).toLocaleString()
                              : 'Never'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4} wrap="nowrap">
                            {(['dosetronic', 'apex_fusion', 'kamoer_stp', 'kamoer_p4', 'kamoer_kh'].includes(device.device_type)) && (
                              <Tooltip label="Pump Assignments">
                                <ActionIcon
                                  variant="subtle"
                                  color="blue"
                                  onClick={() => setPumpAssignmentDevice(device)}
                                >
                                  <IconPencil size={16} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                            <DeviceActionsMenu
                              device={device}
                              onTest={handleTest}
                              onEdit={handleEdit}
                              onDelete={handleDelete}
                            />
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Paper>
            </Box>

            {/* Mobile: Card Layout */}
            <Box hiddenFrom="sm">
              <Stack gap="sm">
                {devices.map((device) => (
                  <DeviceCard
                    key={device.id}
                    device={device}
                    onClick={() => handleCardTap(device)}
                  />
                ))}
              </Stack>
            </Box>
          </>
        )}

        {/* Add Device Modal */}
        <Modal
          opened={addModalOpened}
          onClose={() => setAddModalOpened(false)}
          title="Add Device"
          size="md"
        >
          <AddDeviceForm
            tankId={selectedTank.id}
            existingDevices={devices}
            onSuccess={() => setAddModalOpened(false)}
            onDuplicate={handleDuplicate}
            onCancel={() => setAddModalOpened(false)}
          />
        </Modal>

        {/* Edit Device Modal */}
        <Modal
          opened={editModalOpened}
          onClose={() => setEditModalOpened(false)}
          title="Edit Device"
          size="md"
        >
          <Stack gap="md">
            {formError && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="red"
                onClose={() => setFormError(null)}
                withCloseButton
              >
                {formError}
              </Alert>
            )}

            {editingLinkedKamoer ? (
              <>
                <Alert color="blue" icon={<IconInfoCircle size={16} />}>
                  Please note: updating these credentials will update the Kamoer account used by all linked Kamoer devices in this tank.
                </Alert>

                <Divider label="Kamoer Account" labelPosition="center" />

                <TextInput
                  label="Kamoer Email"
                  value={editAccountEmail}
                  onChange={(event) => setEditAccountEmail(event.currentTarget.value)}
                  disabled={isSubmitting}
                  required
                />

                <PasswordInput
                  label="New Kamoer Password"
                  placeholder="Leave blank to keep current password"
                  value={editAccountPassword}
                  onChange={(event) => setEditAccountPassword(event.currentTarget.value)}
                  disabled={isSubmitting}
                  autoComplete="new-password"
                />
              </>
            ) : (
              <Alert color="blue" icon={<IconAlertCircle size={16} />}>
                For security, you must re-enter credentials to update this device.
              </Alert>
            )}

            <TextInput
              label="Device Type"
              value={deviceType ? DEVICE_TYPE_OPTIONS.find(opt => opt.value === deviceType)?.label || deviceType : ''}
              disabled
              readOnly
            />

            <TextInput
              label="Display Name"
              placeholder="My Apex Controller"
              required
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={isSubmitting}
            />

            <Switch
              label="Data Collection"
              description={
                isEnabled
                  ? 'Enabled - Device will sync data automatically'
                  : 'Disabled - No new data will be collected'
              }
              checked={isEnabled}
              onChange={(e) => setIsEnabled(e.currentTarget.checked)}
              disabled={isSubmitting}
            />

            {!editingLinkedKamoer && (
              <>
                <Divider label="Credentials" labelPosition="center" />

                {deviceType && (
                  <CredentialFields
                    type={deviceType}
                    username={username}
                    email={email}
                    password={password}
                    enableWaterTests={enableWaterTests}
                    enableMaintenance={enableMaintenance}
                    disabled={isSubmitting}
                    onUsernameChange={setUsername}
                    onEmailChange={setEmail}
                    onPasswordChange={setPassword}
                    onEnableWaterTestsChange={setEnableWaterTests}
                    onEnableMaintenanceChange={setEnableMaintenance}
                  />
                )}

                {deviceType === 'aquarimate' && (
                  <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light" mt="md">
                    Data collection options above reset to defaults. Update if needed.
                  </Alert>
                )}
              </>
            )}

            <Group justify="flex-end" gap="xs">
              <Button
                variant="subtle"
                onClick={() => setEditModalOpened(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button onClick={handleSubmitEdit} loading={isSubmitting}>
                Save Changes
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          opened={deleteModalOpened}
          onClose={() => {
            setDeleteModalOpened(false);
            setDeleteMode(null);
          }}
          title="Delete Device Configuration"
          size="md"
        >
          <Stack gap="md">
            {formError && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="red"
                onClose={() => setFormError(null)}
                withCloseButton
              >
                {formError}
              </Alert>
            )}

            <Text>
              What would you like to do with{' '}
              <Text component="span" fw={500}>
                {selectedForDelete?.display_name}
              </Text>
              ?
            </Text>

            <Radio.Group
              value={deleteMode ?? ''}
              onChange={(value) => setDeleteMode(value as 'delete_data' | 'disable')}
            >
              <Stack gap="sm">
                <Radio
                  value="disable"
                  label="Keep historical data, stop syncing (Recommended)"
                  description="The device will be disabled and no new data will be collected. Historical data remains visible in charts and reports."
                />
                <Radio
                  value="delete_data"
                  label="Delete all historical data"
                  description="This will permanently delete all measurements collected by this device. This action cannot be undone."
                  color="red"
                />
              </Stack>
            </Radio.Group>

            <Group justify="flex-end" gap="xs">
              <Button
                variant="subtle"
                onClick={() => {
                  setDeleteModalOpened(false);
                  setDeleteMode(null);
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                color={deleteMode === 'delete_data' ? 'red' : 'blue'}
                onClick={handleSubmitDelete}
                loading={isSubmitting}
                disabled={deleteMode === null}
              >
                {deleteMode === 'delete_data' ? 'Delete Device & Data' : 'Disable Device'}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Existing Device Found Modal */}
        <Modal
          opened={showUpdateExistingModal}
          onClose={() => {
            setShowUpdateExistingModal(false);
            setDuplicateDevices([]);
            setDuplicatePayload(null);
          }}
          title={duplicateDevices.length > 1 ? "Existing Devices Found" : "Existing Device Found"}
          size="md"
        >
          <Stack gap="md">
            {formError && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="red"
                onClose={() => setFormError(null)}
                withCloseButton
              >
                {formError}
              </Alert>
            )}

            {/* Single device case */}
            {!duplicatePayload?.isBulk && duplicateDevices[0] && (
              <Text>
                You already have a{' '}
                <Text component="span" fw={500}>
                  {duplicateDevices[0].display_name || duplicateDevices[0].device_type}
                </Text>{' '}
                device configured for this tank.
              </Text>
            )}

            {/* Multiple devices case (Focustronic / Kamoer bulk) */}
            {duplicatePayload?.isBulk && (
              <>
                <Text>
                  You already have the following device{duplicateDevices.length > 1 ? 's' : ''} configured for this tank:
                </Text>
                <List size="sm" spacing="xs">
                  {duplicateDevices.map(d => (
                    <List.Item key={d.id}>
                      <Text component="span" fw={500}>{d.display_name || d.device_type}</Text>
                      {!d.is_enabled && <Badge size="xs" color="yellow" ml="xs">Disabled</Badge>}
                    </List.Item>
                  ))}
                </List>
              </>
            )}

            {/* Show disabled warning for single device */}
            {!duplicatePayload?.isBulk && duplicateDevices[0] && !duplicateDevices[0].is_enabled && (
              <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
                The existing device is currently disabled. Updating will re-enable it.
              </Alert>
            )}

            {/* Show disabled warning for multiple devices */}
            {duplicatePayload?.isBulk && duplicateDevices.some(d => !d.is_enabled) && (
              <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
                {duplicateDevices.filter(d => !d.is_enabled).length === duplicateDevices.length
                  ? 'All existing devices are currently disabled. Updating will re-enable them.'
                  : 'Some existing devices are currently disabled. Updating will re-enable them.'}
              </Alert>
            )}

            <Text size="sm" c="dimmed">
              We only support one device of each type per tank. Would you like to
              update the existing device{duplicateDevices.length > 1 ? 's' : ''} with new credentials?
            </Text>

            <List size="sm" spacing="xs">
              <List.Item>Your historical data will be preserved</List.Item>
              <List.Item>New data will continue in the same charts</List.Item>
              <List.Item>The old credentials will be replaced</List.Item>
            </List>

            <Group justify="flex-end" gap="xs">
              <Button
                variant="subtle"
                onClick={() => {
                  setShowUpdateExistingModal(false);
                  setDuplicateDevices([]);
                  setDuplicatePayload(null);
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button onClick={handleUpdateExisting} loading={isSubmitting}>
                Update Existing Device{duplicateDevices.length > 1 ? 's' : ''}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Test Connection Modal */}
        <Modal
          opened={testModalOpened}
          onClose={() => setTestModalOpened(false)}
          title="Test Connection"
          size="md"
        >
          <Stack gap="md">
            <Text>
              Testing connection to{' '}
              <Text component="span" fw={500}>
                {selectedForTest?.display_name}
              </Text>
            </Text>

            {testResult && (
              <Alert
                icon={testResult.success ? <IconCheck size={16} /> : <IconX size={16} />}
                color={testResult.success ? 'green' : 'red'}
              >
                {testResult.message}
              </Alert>
            )}

            <Group justify="flex-end" gap="xs">
              <Button
                variant="subtle"
                onClick={() => setTestModalOpened(false)}
                disabled={isSubmitting}
              >
                Close
              </Button>
              {!testResult && (
                <Button onClick={handleSubmitTest} loading={isSubmitting}>
                  Run Test
                </Button>
              )}
            </Group>
          </Stack>
        </Modal>

        {/* Device Detail Modal (Mobile) */}
        <DeviceDetailModal
          device={selectedForDetail}
          opened={detailModalOpened}
          onClose={handleDetailClose}
          onTest={handleDetailTest}
          onEdit={handleDetailEdit}
          onDelete={handleDetailDelete}
          onPumpAssignments={setPumpAssignmentDevice}
        />

        {/* Pump Assignment Overlay (Dosetronic & Apex DOS) */}
        {pumpAssignmentDevice && (
          <PumpAssignmentOverlay
            opened={!!pumpAssignmentDevice}
            onClose={() => setPumpAssignmentDevice(null)}
            deviceId={pumpAssignmentDevice.id}
            deviceName={pumpAssignmentDevice.display_name}
          />
        )}
      </Stack>
    </Container>
  );
};
